import {
  useAccessTokenReceived,
  useCustomMutation,
  useGrippContext,
  useInvalidateConfig,
  useSendAuthCode,
  useStorageKeys,
} from '@gripp/shared-logic';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, Text, View } from 'react-native';
import { OtpInput } from 'react-native-otp-entry';
import { typography } from '../../components/crop-circle/tokens';
import { getFormattedPhoneNumber } from '../../components/forms/controlledPhoneInput';
import { Colors } from '../../themes/colors';
import { LoginProps } from './props';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export const VerifyAuthCode = (props: LoginProps) => {
  const { t } = useTranslation();

  const { mutateAsync } = useCustomMutation({
    query: validateAuthCode,
  });

  const { sendAuthCodeAsync } = useSendAuthCode();

  const [sentTo, setSentTo] = useState<string>('');
  const [hasError, setHasError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const otpRef = useRef<any>(null);

  const { storageHandler } = useGrippContext();
  const storageKeys = useStorageKeys();
  const onAccessTokenReceived = useAccessTokenReceived();
  const invalidateConfig = useInvalidateConfig();

  const getAuthCodeInput = async () => {
    return JSON.parse(
      (await storageHandler.getItem(storageKeys.authCodeInput)) as string
    );
  };

  useEffect(() => {
    const getSentTo = async () => {
      const authCodeInput = await getAuthCodeInput();
      setSentTo(
        authCodeInput.phone
          ? getFormattedPhoneNumber(
              authCodeInput.phone.countryCode,
              authCodeInput.phone.callingCode,
              authCodeInput.phone.phoneNumber
            )
          : authCodeInput.email
      );
    };

    getSentTo();
  }, []);

  useEffect(() => {
    if (hasError && !isSubmitting) {
      otpRef.current?.focus?.();
    }
  }, [hasError, isSubmitting]);

  const onResendCode = async () => {
    await sendAuthCodeAsync({ ...(await getAuthCodeInput()) }, () => {});
    setHasError(false);
    otpRef.current?.clear?.();
  };

  const submitCode = async (code: string) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const input = {
      ...JSON.parse(
        (await storageHandler.getItem(storageKeys.authCodeInput)) as string
      ),
      code,
    };
    await mutateAsync(
      { input },
      {
        onSettled: async (data: any) => {
          const result = data?.authentication?.validateAuthCode;

          if (result?.success) {
            const redirectPath = await storageHandler.getItem(
              storageKeys.redirectPath
            );
            const { accessToken } = result;
            await onAccessTokenReceived(accessToken, false, true);
            await invalidateConfig();

            if (props.handleRedirect) {
              if (props.isNewAccount) {
                await storageHandler.setItem(storageKeys.newRegister, '');
                await props.handleRedirect('/onboarding');
              } else {
                await props.handleRedirect(redirectPath || '/');
              }
            }
          } else {
            setHasError(true);
          }

          setIsSubmitting(false);
        },
      }
    );
  };

  const onFilled = (code: string) => {
    setHasError(false);
    submitCode(code);
  };

  return (
    <View style={styles.container}>
      <View style={styles.headerSection}>
        <Text style={styles.verifyTitle}>{t('login.verify')}</Text>
        <Text style={styles.verifySubtitle}>
          {t('login.verifyCodeSentTo')} {sentTo}
        </Text>
      </View>

      <OtpInput
        ref={otpRef}
        autoFocus
        numberOfDigits={4}
        disabled={isSubmitting}
        focusColor={hasError ? Colors.redText : Colors.role.textStrong}
        onFilled={onFilled}
        onTextChange={() => {
          if (hasError) setHasError(false);
        }}
        theme={{
          containerStyle: { ...styles.otpContainer, gap: 16 },
          pinCodeContainerStyle: hasError
            ? styles.pinCodeContainerError
            : styles.pinCodeContainer,
          pinCodeTextStyle: hasError
            ? styles.pinCodeTextError
            : styles.pinCodeText,
        }}
      />

      <View style={styles.resendTextContainer}>
        <Text style={styles.resendText}>{t('login.didntReceiveCode')}</Text>
        <Text style={styles.resendText}>
          <Text style={styles.resendLink} onPress={onResendCode}>
            {t('login.resendCode')}
          </Text>
          {'  '}
          {t('or')}
          {'  '}
          <Text
            style={styles.resendLink}
            onPress={() => props.onTryAnotherMethod?.()}
          >
            {t('login.tryAnotherMethod')}
          </Text>
        </Text>
      </View>
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    alignSelf: 'stretch',
    gap: 40,
  },
  headerSection: {
    gap: 8,
    alignItems: 'center' as const,
  },
  verifyTitle: {
    ...(Platform.OS === 'web'
      ? typography.display.largeEmphasised
      : typography.display.smallEmphasised),
    textAlign: 'center',
    color: role.textStrong,
  },
  verifySubtitle: {
    ...typography.body.small,
    textAlign: 'center',
    color: role.textStrong,
  },
  otpContainer: {
    alignSelf: 'stretch',
  },
  pinCodeContainer: {
    backgroundColor: role.surface,
    height: 52,
    flex: 1,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: role.borderControl,
  },
  pinCodeContainerError: {
    backgroundColor: role.surface,
    height: 52,
    flex: 1,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.redText,
  },
  // react-native-otp-entry's own `codeText` style sets no colour at all, so the
  // digits fall back to RN's default black and vanish on a dark surface. A
  // colour has to be passed in every case, not only the error one.
  pinCodeText: {
    color: role.textStrong,
  },
  pinCodeTextError: {
    color: Colors.redText,
  },
  resendTextContainer: {
    gap: 8,
    alignItems: 'center' as const,
  },
  resendText: {
    ...typography.body.small,
    color: role.textSoft,
    textAlign: 'center',
  },
  resendLink: {
    ...typography.body.small,
    color: role.textSoft,
    textDecorationLine: 'underline',
  },
}));

const validateAuthCode = `
  mutation Mutation($input: ValidateAuthCodeInput!) {
    authentication {
      validateAuthCode(input: $input) {
        success
        message
        accessToken
      }
    }
  }
`;
